/* eslint-disable no-undef */
import { IconButton } from '@material-ui/core';
import { Close, GetApp, KeyboardArrowDown } from '@material-ui/icons';
import { FC, useCallback, useRef, useState } from 'react';
import { apiServiceNative } from '../../../../../utils/fetchNative';
import { useAuthStyles } from '../../Auth.styles';
import { useHelpModalFormStyles } from './HelpModalForm.styles';

const SUBJECTS = ['Signaler un bug', 'Problème de connexion', 'Question sur un test', 'Autre'];
const ACCEPTED_FORMATS = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
const MAX_FILE_SIZE_MB = 100;

interface HelpModalFormProps {
  onClose: () => void;
  onSuccess: () => void;
  defaultEmail?: string;
  defaultName?: string;
  defaultFirstName?: string;
}

export const HelpModalForm: FC<HelpModalFormProps> = ({
  onClose,
  onSuccess,
  defaultEmail = '',
  defaultName = '',
  defaultFirstName = '',
}) => {
  const shared = useAuthStyles();
  const local = useHelpModalFormStyles();
  const cls = { ...shared, ...local };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    email: defaultEmail,
    nom: defaultName,
    prenom: defaultFirstName,
    sujet: '',
    commentaire: '',
  });
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [field]: e.target.value });
    setSubmitError('');
  };

  const validateFile = useCallback((f: File): boolean => {
    setFileError('');
    const ext = f.name.split('.').pop()?.toLowerCase() || '';
    const validExts = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];
    if (!validExts.includes(ext)) {
      setFileError('Format non accepté. Utilisez PDF, DOC, DOCX, JPG ou PNG.');
      return false;
    }
    if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setFileError('Le fichier dépasse la taille maximale de 100 Mo.');
      return false;
    }
    return true;
  }, []);

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    },
    [validateFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected && validateFile(selected)) {
        setFile(selected);
      }
    },
    [validateFile]
  );

  const handleSubmit = async () => {
    if (!form.email || !form.nom || !form.prenom || !form.sujet || !form.commentaire) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formData = new FormData();
      formData.append('email', form.email);
      formData.append('nom', form.nom);
      formData.append('prenom', form.prenom);
      formData.append('sujet', form.sujet);
      formData.append('commentaire', form.commentaire);
      if (file) {
        formData.append('screenshot', file);
      }

      await apiServiceNative.post('/api/users-permissions/user/help-request', formData, true);
      onSuccess();
    } catch {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = form.email && form.nom && form.prenom && form.sujet && form.commentaire;

  return (
    <div className={cls.helpContent}>
      <div className={cls.helpHeaderBlock}>
        <div className={cls.helpHeader}>
          <h2 id="help-modal-title" className={cls.helpTitle}>
            Besoin d&apos;aide
          </h2>
          <IconButton className={cls.helpCloseButton} onClick={onClose} aria-label="Fermer">
            <Close />
          </IconButton>
        </div>
        <p className={cls.helpDescription}>
          Notre équipe est à votre écoute. Merci de nous préciser votre demande via le formulaire
          ci-dessous afin que nous puissions vous accompagner au mieux.
        </p>
      </div>

      <div className={cls.form}>
        <div className={cls.fieldsGroup}>
          {!defaultEmail && (
            <label htmlFor="help-email" className={cls.field}>
              <span className={cls.label}>Email</span>
              <input
                id="help-email"
                type="email"
                className={cls.input}
                value={form.email}
                onChange={handleChange('email')}
                placeholder="Votre adresse email"
              />
            </label>
          )}

          <label htmlFor="help-nom" className={cls.field}>
            <span className={cls.label}>Nom</span>
            <input
              id="help-nom"
              type="text"
              className={cls.input}
              value={form.nom}
              onChange={handleChange('nom')}
              placeholder="Votre nom"
            />
          </label>

          <label htmlFor="help-prenom" className={cls.field}>
            <span className={cls.label}>Prénom</span>
            <input
              id="help-prenom"
              type="text"
              className={cls.input}
              value={form.prenom}
              onChange={handleChange('prenom')}
              placeholder="Votre prénom"
            />
          </label>

          <label htmlFor="help-sujet" className={cls.field}>
            <span className={cls.label}>Sujet</span>
            <div className={cls.helpSelectWrapper}>
              <select
                id="help-sujet"
                className={cls.helpSelect}
                value={form.sujet}
                onChange={handleChange('sujet')}>
                <option value="">Sélectionnez un sujet</option>
                {SUBJECTS.map(subject => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
              <KeyboardArrowDown className={cls.helpSelectIcon} />
            </div>
          </label>

          <label htmlFor="help-commentaire" className={cls.field}>
            <span className={`${cls.label} ${cls.helpLabelDark}`}>Commentaire</span>
            <textarea
              id="help-commentaire"
              className={cls.helpTextarea}
              value={form.commentaire}
              onChange={handleChange('commentaire')}
              placeholder="Décrivez votre problème..."
              rows={4}
            />
          </label>
        </div>

        <label htmlFor="help-screenshot" className={cls.field}>
          <div className={cls.helpLabelRow}>
            <span className={`${cls.label} ${cls.helpLabelDark}`}>Capture d&apos;écran</span>
            <span className={cls.helpOptional}>Facultatif</span>
          </div>
          <div
            className={`${cls.helpDropZone} ${isDragging ? cls.helpDropZoneActive : ''} ${
              fileError ? cls.helpDropZoneError : ''
            }`}
            onDragOver={e => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleFileDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                fileInputRef.current?.click();
              }
            }}
            role="button"
            tabIndex={0}>
            <input
              ref={fileInputRef}
              id="help-screenshot"
              type="file"
              accept={ACCEPTED_FORMATS}
              onChange={handleFileSelect}
              hidden={true}
            />
            {file ? (
              <div className={cls.helpFileInfo}>
                <span className={cls.helpFileName}>{file.name}</span>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setFile(null);
                    setFileError('');
                  }}
                  aria-label="Supprimer le fichier">
                  <Close fontSize="small" />
                </IconButton>
              </div>
            ) : (
              <>
                <div className={cls.helpUploadIconWrapper}>
                  <GetApp className={cls.helpUploadIcon} />
                </div>
                <span className={cls.helpUploadText}>Cliquez ou faites glisser ici pour importer</span>
                <span className={cls.helpUploadFormats}>
                  Formats acceptés : PDF, DOC, DOCX, JPG, PNG (max. 100Mo)
                </span>
              </>
            )}
          </div>
          {fileError && <p className={cls.helpErrorText}>{fileError}</p>}
        </label>

        {submitError && <p className={cls.helpErrorText}>{submitError}</p>}

        <button
          type="button"
          className={cls.primaryButton}
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}>
          {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
        </button>
      </div>
    </div>
  );
};
